import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { createFilterQuery } from "@via/api/integration-tokens-filter-query";
import { useNotifications } from "@via/hooks/useNotifications";
import { ErrorNotification } from "@via/utils/interfaces";
import {
  CreateIntegrationTokenPayload,
  EditIntegrationTokenPayload,
  ListServiceTokensResponse,
  ServiceToken,
} from "common";
import { useImpersonationUser } from "@via/hooks/useImpersonationUser.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";

export const fetchIntegrationTokens = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>,
  userId?: number,
  includeViaTokens: boolean = false
): Promise<ListServiceTokensResponse> => {
  try {
    const baseUrl = "/api/user/v1/service-tokens";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    if (filter && filter.length > 0) {
      const queryFilter = createFilterQuery(filter);
      queryParams.append("filter", queryFilter);
    }
    if (userId) {
      queryParams.append("userId", userId.toString());
    }
    if (includeViaTokens) {
      queryParams.append("includeViaTokens", includeViaTokens.toString());
    }
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useIntegrationTokens = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filters: Array<{ id: string; value: unknown }> = [],
  includeViaTokens: boolean = false
) => {
  const skip = page * pageSize;
  const take = pageSize;
  const impersonationId = useImpersonationUser();

  return useQuery({
    queryKey: [
      "/api/user/v1/service-tokens",
      take,
      skip,
      sort,
      filters,
      impersonationId,
      includeViaTokens,
    ],
    queryFn: () =>
      fetchIntegrationTokens(
        take,
        skip,
        sort,
        filters,
        impersonationId,
        includeViaTokens
      ),
    retry: false,
  });
};

export const fetchIntegrationTokenById = async (
  id: number,
  includeViaTokens: boolean = true
): Promise<ServiceToken> => {
  const baseUrl = `/api/user/v1/service-tokens/${id}`;
  const queryParams = new URLSearchParams();
  if (includeViaTokens) {
    queryParams.append("includeViaTokens", includeViaTokens.toString());
  }
  const url = queryParams.toString()
    ? `${baseUrl}?${queryParams.toString()}`
    : baseUrl;
  const res = await axios.get(url);
  return res.data;
};

export const useIntegrationTokenById = (id: number | null) => {
  return useQuery({
    queryKey: ["/api/user/v1/service-tokens/:id", id],
    queryFn: () => fetchIntegrationTokenById(id as number),
    // With no login selected there is nothing to fetch. Without this guard the
    // drawer requests ".../service-tokens/null" and the server answers 400.
    enabled: id != null,
    retry: false,
  });
};

export const useCreateIntegrationToken = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  const impersonationId = useImpersonationUser();

  return useMutation({
    mutationFn: async (payload: CreateIntegrationTokenPayload) => {
      const res = await axios.post("/api/user/v1/service-tokens", payload, {
        params: {
          ...(impersonationId && { userId: impersonationId }),
        },
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "integration-token-created",
        variant: NOTIFICATION.SUCCESS,
        message: "Integration token created successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/service-tokens"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "integration-token-creation-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to create integration token",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useDeleteIntegrationToken = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete(`/api/user/v1/service-tokens/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/service-tokens"],
      });
      setNotification({
        id: "integration-token-deleted-successfully",
        variant: NOTIFICATION.SUCCESS,
        message: "Integration token deleted successfully",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "integration-token-deletion-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to delete integration token",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useUpdateIntegrationToken = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: EditIntegrationTokenPayload) => {
      const res = await axios.put(`/api/user/v1/service-tokens`, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "integration-token-updated",
        variant: NOTIFICATION.SUCCESS,
        message: "Integration token updated successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/service-tokens"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "integration-token-update-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to update integration token",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useSelectIntegrationToken = () => {
  const impersonationId = useImpersonationUser();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.post(
        `/api/user/v1/service-tokens/${impersonationId ? impersonationId : user.id}/select`,
        {
          id,
        }
      );
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "integration-token-selected",
        variant: NOTIFICATION.SUCCESS,
        message: "Integration token selected successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/service-tokens"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "integration-token-selection-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to select integration token",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};
